/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useEffect, useState } from "react";

import styled from "styled-components";

import { ProgrammingLanguage } from "@vienna/common";

import { CodeBlock } from "src/classes/block/block-types";
import { CODE_LANG_MENU_ID } from "src/lib/constants";

import LanguageOption from "./LanguageOption";

interface Props {
  block: CodeBlock;
  closeMenu: () => void;
}

export default function LanguageMenu({ block, closeMenu }: Props) {
  const blockHeight = block.observe((state) => state.height);
  const selectedLanguage = block.observe((state) => state.language);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(
    CodeBlock.supportedLanguages.findIndex((lang) => lang === selectedLanguage)
  );

  useEffect(() => {
    // adds a keydown event listener to capture arrow keys
    document.addEventListener("keydown", handleKeydown, false);
    return () => {
      // remove event listener when block unmounts
      document.removeEventListener("keydown", handleKeydown, false);
    };
  }, [selectedIndex]);

  // when an option is clicked or enter is pressed
  const changeLanguage = (language: ProgrammingLanguage) => {
    block.changeLanguage(language);
    closeMenu();
  };

  const handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      // if there is a next option, increment selected
      setSelectedIndex(Math.min((selectedIndex || 0) + 1, CodeBlock.supportedLanguages.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      // if there is a previous option, decrement selected
      setSelectedIndex(Math.max((selectedIndex || 0) - 1, 0));
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex === null) return;
      const newLanguage = CodeBlock.supportedLanguages[selectedIndex];
      changeLanguage(newLanguage);
    }
    if (e.key === "Escape") {
      closeMenu();
      e.stopPropagation();
    }
  };
  return (
    <StyledLanguageMenu blockHeight={blockHeight} id={CODE_LANG_MENU_ID}>
      {CodeBlock.supportedLanguages.map((language, index) => (
        <LanguageOption
          language={language}
          changeLanguage={changeLanguage}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          index={index}
          key={language}
        />
      ))}
    </StyledLanguageMenu>
  );
}

const StyledLanguageMenu = styled.div<{ blockHeight: number }>`
  position: absolute;
  background-color: ${({ theme }) => theme.background};
  top: ${({ blockHeight }) => `${blockHeight}px`};
  left: 0;
  border: ${({ theme }) => `1px solid ${theme.subtleContrast}`};
  max-height: 180px;
  overflow-y: scroll;
  ::-webkit-scrollbar {
    width: 5px;
  }
`;
